import {
  getInitials,
  Roles 
} from "@theatticusapp/atticus-shared-components";
import { UserProfile, UserProfileJSON } from "./UserProfile";
import { AdvisorGroup, AdvisorGroupJSON } from "./AdvisorGroup";
import { EstateUserInfo, EstateUserInfoJson } from "./EstateUserInfo";
import { intOrUndefined } from "../utils/intOrUndefined"

export enum Countries {
  "us" = 1,
  "ca" = 2,
}

export enum Languages {
  "en" = 1,
  "fr" = 2,
  "vi" = 3,
  "es" = 4,
}

// database field names
export type UserJSON = {
  id?: number;
  cognito_uuid?: string;
  has_completed_onboarding?: boolean;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role_id?: number;
  advisor_group_id?: number;
  advisor_group?: AdvisorGroupJSON;
  countries_id?: number;
  languages_id?: number;
  invite_id?: string;
  user_profile?: UserProfileJSON;
  estate_user_info?: EstateUserInfoJson;
};

// export type CognitoSignUpAttributes = {
//   given_name?: string;
//   middle_name?: string;
//   family_name?: string;
//   phone_number?: string;
//   "custom:invite_id"?: string;
// };

export type CognitoUserJSON = {
  Username?: string;
  UserStatus?: string;
  Enabled?: boolean;
  Attributes?: [
    {
      Name: string;
      Value: string;
    }
  ];
};

export type CognitoUserAttributes = {
  sub?: string;
  given_name?: string;
  middle_name?: string;
  family_name?: string;
  picture?: string;
  email?: string;
  email_verified?: string;
  phone_number?: string;
  phone_number_verified?: string;
  "custom:invite_id"?: string;
  "custom:country_id"?: string;
  "custom:state_id"?: string; // used when advisor creates an estate
  "custom:language_id"?: string;
  "custom:origin"?: string;
  locale?: string;
};

export class User {
  static className = "User";

  constructor(
    public id?: number,
    public cognitoUsername?: string,
    public cognitoGroups?: string[],
    public email?: string,
    public first?: string,
    public middle?: string, // Is this used?
    public last?: string,
    public phone?: string,
    public locale?: string, // Is this used
    public pictureUrl?: string,
    public hasCompletedOnboarding?: boolean,
    public admin?: boolean,
    public advisor?: boolean,
    public roleId?: number,
    public estateUserInfo?: EstateUserInfo,
    public advisorGroup?: AdvisorGroup,
    public advisorGroupId?: number,
    public advisorGroupName?: string, // Keep, but deprecate later for relationship
    public profile?: UserProfile,
    public countryId?: number,
    public stateId?: number,
    public languageId?: number,
    public inviteId?: string,
    public origin?: string,
    public cognitoUser?: any,
    public challengeName?: string
  ) { }

  static fromCognitoWithChallenge(
    username?: string,
    cognitoUser?: any,
    challengeName?: string
  ): User {
    return new User(
      undefined,
      username,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      cognitoUser,
      challengeName
    );
  }

  static fromCognito(json: any): User {
    return new User(
      undefined,
      json.sub,
      undefined,
      json.email,
      json.given_name,
      json.middle_name,
      json.family_name,
      json.phone_number,
      json.locale,
      json.picture,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      intOrUndefined(json["custom:invite_id"]),
      json["custom:origin"],
      undefined,
      undefined
    );
  }

  static fromJSON(json: UserJSON): User {
    return new User(
      json.id,
      undefined,
      undefined,
      json.email,
      json.first_name,
      undefined,
      json.last_name,
      json.phone,
      undefined,
      undefined,
      json.has_completed_onboarding || (json as any).hasCompletedOnboarding, // api returns hasCompletedOnboarding for some reason
      undefined,
      undefined,
      json.role_id,
      json.estate_user_info
        ? EstateUserInfo.fromJSON(json.estate_user_info)
        : undefined,
      json.advisor_group
        ? AdvisorGroup.fromJSON(json.advisor_group)
        : undefined,
      json.advisor_group?.id,
      json.advisor_group?.name,
      json.user_profile ? UserProfile.fromJSON(json.user_profile)||undefined : undefined,
      json.countries_id,
      json.languages_id,
      intOrUndefined(json.invite_id),
      undefined,
      undefined
    );
  }

  toJson(): UserJSON {
    return {
      id: this.id,
      cognito_uuid: this.cognitoUsername,
      has_completed_onboarding: this.hasCompletedOnboarding,
      email: this.email || "",
      phone: this.phone || "",
      role_id: this.roleId,
      advisor_group_id: this.advisorGroupId,
      advisor_group: this.advisorGroup?.toJSON(),
      countries_id: this.countryId,
      languages_id: this.languageId,
      invite_id: this.inviteId,
      user_profile: this.profile?.toJSON(),
      estate_user_info: this.estateUserInfo?.toJSON(),
    };
  }

  // currently only support US and CA
  // get countryId(): string {
  //   if (this.locale?.includes("ca")) {
  //     return Countries.ca;
  //   } else {
  //     return Countries.us;
  //   }
  // }

  // currently only support EN and FR
  // get languageId(): string {
  //   if (this.locale?.includes("en")) {
  //     return Languages.en;
  //   } else {
  //     return Languages.fr;
  //   }
  // }

  get initials(): string {
    let initials = "";
    const firstName = this?.first || this?.profile?.firstName;
    const lastName = this?.last || this?.profile?.lastName;
    if (firstName && lastName) {
      initials = getInitials(`${firstName} ${lastName}`);
    }
    return initials;
  }

  get fullName(): string | undefined {
    return this.profile
      ? this.profile.fullName
      : this.first || this.last
        ? `${this.first} ${this.last}`
        : undefined;
  }

  get selectName(): string {
    return (
      (this?.first && this?.last
        ? this?.first + " " + this?.last
        : this?.email) || "undefined"
    );
  }

  get isAdvisor(): boolean {
    return this.roleId === Roles.Advisor;
  }

  // user role must be Advisor Admin or higher
  get isAdvisorAdmin(): boolean {
    return (
      this.roleId === Roles.Advisor_Admin ||
      this.roleId === Roles.Super_Advisor_Admin ||
      this.roleId === Roles.Uber_Admin
    );
  }

  get isUberAdmin(): boolean {
    return this.roleId === Roles.Uber_Admin;
  }

  get role(): string | undefined {
    return (
      (this?.roleId && Roles[this.roleId].replaceAll("_", " ")) || undefined
    );
  }

  get metricsTool(): boolean | undefined {
    return false; //this.cognitoGroups?.includes(SpecialCognitoGroups.MetricsTool);
  }

  get onlyMetricsTool(): boolean | undefined {
    return false;//this.cognitoGroups?.includes(SpecialCognitoGroups.OnlyMetricsTool);
  }

  get onlyAdminTool(): boolean | undefined {
    return false;//this.cognitoGroups?.includes(SpecialCognitoGroups.OnlyAdminTool);
  }

  get hasInviteAccess(): boolean {
    return !(this.onlyMetricsTool || this.onlyAdminTool);
  }

  // everyone can see estate access unless they are limited to "OnlyMetricsTool" || "OnlyAdminTool"
  get hasEstateAccess(): boolean {
    return !(this.onlyMetricsTool || this.onlyAdminTool);
  }

  // only Uber and "OnlyMetricsTool" have access
  get hasMetricsToolAccess(): boolean {
    return (
      !!this.onlyMetricsTool ||
      !!this.metricsTool ||
      (this.isUberAdmin && !this.onlyAdminTool)
    );
  }

  // all Uber and "OnlyAdminTool" users
  get hasAdminToolAccess(): boolean {
    return this.isUberAdmin || !!this.onlyAdminTool;
  }

  get hasAssignmentToolAccess(): boolean {
    return this.isUberAdmin;
  }
}
